import { flow, action } from 'mobx';
import { ClientLogger } from 'bernie-client';
import {
  codeSplit,
  Controller,
  FetchOptions,
  FetchPageOptions,
  ContextStore,
  StoreContainer,
  AnalyticsStore,
  PageStore
} from 'bernie-core';
import { getBexApiContext } from 'bernie-context';
import { Localization } from 'bernie-l10n';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';

import {
  BannersStore,
  DialogsStore,
  ErrorStore,
  PageTitleStore,
  SearchResultsStore,
  SearchFormStore,
  SearchCriteriaStore,
  SortFilterStore,
  ListingsStore,
  OpinionLabStore,
  FlexibleSearchResultsStore,
  FlexibleSearchStore,
  ClientMetadataStore,
  CheapestListingInfoStore,
  StepIndicatorStore,
  GaiaIdStore
} from 'stores';
import {
  FlightsQueryState,
  FlightsShoppingPwaFlightSearchResultsFlightsSearch,
  FlightsFlexibleSearch
} from '__generated__/api';
import { FlexSearchPerformance, generateRequestOptions, needSearchFsr, PageId, FightsSearchPageIds } from 'utility';
import { SearchQueryAdapter } from 'stores/adapters';
import analyticsWrapper from 'utility/analytics-wrapper';

export class FlightsSearchController implements Controller {
  public pageId = PageId.FIGHTS_SEARCH_ONE_WAY;
  public path = '/Flights-Search*';
  public routeName = 'flights-search';
  public bundles = [];
  public exact = true;
  public component = codeSplit(() => import(/* webpackChunkName: "search" */ '../views/flights-search'));
  public pendingSearchResultsPromises = [];
  public pendingFlexibleSearchResultsPromises = [];
  public clientErrorRenderDelay = 500;
  private logger = ClientLogger.getLoggerWithIdentifier('controller.flightsSearch');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public fetch(options: FetchOptions): Promise<any> {
    const { searchCriteriaStore, pageStore } = this.getFetchVariables(options.stores, options.isServer);
    const query = options.query;
    const tripType = SearchQueryAdapter.mapTripType(query.trip);
    pageStore.pageId = FightsSearchPageIds[`${tripType}_${query.pageId || 0}`] || this.pageId;

    if (options.isServer) {
      searchCriteriaStore.setCriteriaFromUrl(options);

      return this.fetchServerSide(options);
    }

    //TODO: Catch errors inside needSearch https://jira.expedia.biz/browse/FODF-858
    const searchNeeded = needSearchFsr(options, searchCriteriaStore);
    if (searchNeeded) {
      const promises = [];

      promises.push(this.fetchSearchResultsClientSide(options));
      if (searchCriteriaStore.fetchFlexResults) {
        promises.push(this.fetchFlexibleSearchResultsClientSide(options));
      }

      return Promise.all(promises);
    }

    const bannersStore = options.stores.get<BannersStore>('bannersStore');
    bannersStore.setIsDialogEvent(!searchNeeded);

    return Promise.resolve();
  }

  private async fetchServerSide(options: FetchOptions) {
    const {
      searchCriteriaStore,
      searchFormStore,
      listingsStore,
      searchResultsStore,
      sortFilterStore,
      opinionLabStore,
      pageTitleStore,
      errorStore,
      analyticsStore,
      variables,
      clientMetadataStore,
      stepIndicatorStore,
      gaiaIdStore,
      pageStore
    } = this.getFetchVariables(options.stores, options.isServer);

    const response = await searchResultsStore.fetchLoading(
      variables,
      generateRequestOptions(options.request, options.isServer, pageStore.pageId),
      analyticsStore
    );

    if (!response) {
      return;
    }

    const {
      searchForm,
      sortAndFilterResult,
      listingResult,
      pageTitle,
      flightsOpinionLab,
      clientMetadata,
      gaiaId,
      stepIndicator
    } = response;

    searchFormStore.hydrate({ searchForm });
    listingsStore.hydrate(listingResult);
    pageTitleStore.hydrate(pageTitle);
    opinionLabStore.hydrate(flightsOpinionLab);
    sortFilterStore.hydrate(sortAndFilterResult);
    clientMetadataStore.hydrate(clientMetadata);
    gaiaIdStore.hydrate({ gaiaId });
    stepIndicatorStore.hydrate(stepIndicator);
    sortFilterStore.saveInitialSkeletons();
    searchCriteriaStore.forceFetch = false;
    listingsStore.loading = true;
    errorStore.noApiResponse = false;
  }

  private fetchSearchResultsClientSide(options: FetchOptions) {
    if (this.pendingSearchResultsPromises.length > 0) {
      this.pendingSearchResultsPromises.forEach(p => p.cancel());
    }
    const clientPromise = this.createFlowForSearchResultsFetch(options);

    clientPromise.catch(error => {
      this.logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'ENVERROR'), error);
    });

    this.pendingSearchResultsPromises.push(clientPromise);

    return clientPromise;
  }

  private fetchFlexibleSearchResultsClientSide(options: FetchOptions) {
    if (this.pendingFlexibleSearchResultsPromises.length > 0) {
      this.pendingFlexibleSearchResultsPromises.forEach(p => p.cancel());
    }
    const clientPromise = this.createFlowForFlexSearchFetch(options);

    clientPromise.catch(error => {
      this.logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'ENVERROR'), error);
    });

    this.pendingFlexibleSearchResultsPromises.push(clientPromise);

    return clientPromise;
  }

  @action
  private createFlowForSearchResultsFetch = flow(function* (options: FetchOptions) {
    const {
      searchCriteriaStore,
      searchFormStore,
      listingsStore,
      searchResultsStore,
      sortFilterStore,
      opinionLabStore,
      pageTitleStore,
      errorStore,
      bannersStore,
      dialogsStore,
      analyticsStore,
      variables,
      clientMetadataStore,
      cheapestListingInfoStore,
      gaiaIdStore,
      pageStore,
      stepIndicatorStore
    } = this.getFetchVariables(options.stores, options.isServer);
    const requestOptions = generateRequestOptions(options.request, options.isServer, pageStore.pageId);

    analyticsWrapper.resetSearchResultsAnalyticsPayLoad(analyticsStore);

    listingsStore.listings = [];
    dialogsStore.dialogs = [];
    sortFilterStore.addSkeletonsOnLoading();

    listingsStore.loading = true;
    pageTitleStore.setLoading(!(variables.searchFilterValuesList && variables.searchFilterValuesList.length > 0));
    searchFormStore.setLoading(variables);
    searchFormStore.clearErrorRef();

    errorStore.clientFetchFailure = ErrorStore.DEFAULTS.clientFetchFailure;

    try {
      const response: FlightsShoppingPwaFlightSearchResultsFlightsSearch.FlightsSearch = yield searchResultsStore.fetchResults(
        variables,
        requestOptions,
        analyticsStore
      );
      const { isSubsequentialSearch } = searchResultsStore;

      const searchForm = response?.searchForm;
      const sortAndFilterResult = response.sortAndFilterResult;
      const listingResult = (response && response.listingResult) || ListingsStore.DEFAULTS;
      const banners = response && response.banners;
      const dialogs = response && response.dialogs;
      const noListingMessaging = response && response.noListingMessaging;
      const failedRequestMessaging = response && response.failedRequestMessaging;
      const clientMetadata = response && response.clientMetadata;
      const cheapestListingInfo = response && response.cheapestListingInfo;
      const gaiaId = response && response.gaiaId;
      const stepIndicator = response?.stepIndicator;

      searchFormStore.clearErrorRef();
      searchFormStore.validateAndRestrict(searchForm);
      searchFormStore.hydrate({ searchForm });
      sortFilterStore.hydrate(sortAndFilterResult);
      listingsStore.hydrate(listingResult);
      pageTitleStore.hydrate(response && response.pageTitle);
      opinionLabStore.hydrate(response && response.flightsOpinionLab);
      bannersStore.hydrate({ banners });
      dialogsStore.hydrate({ dialogs });
      dialogsStore.setDialogsStoreInitialState();
      errorStore.hydrate({ noListingMessaging, failedRequestMessaging });
      clientMetadataStore.hydrate(clientMetadata);
      cheapestListingInfoStore.hydrate(cheapestListingInfo);
      gaiaIdStore.hydrate({ gaiaId });
      searchCriteriaStore.syncWithSearchResults(response);

      if (isSubsequentialSearch) {
        stepIndicatorStore.hydrate(stepIndicator);
        searchResultsStore.setIsSubsequentialSearch();
      }

      listingsStore.loading = false;
      pageTitleStore.loading = false;
      searchFormStore.loading = false;
      this.pendingSearchResultsPromises = [];

      searchFormStore.setSearchFormInitialState();

      return;
    } catch (error) {
      setTimeout(() => {
        errorStore.clientFetchFailure = true;
        listingsStore.loading = false;
        pageTitleStore.loading = false;
        searchFormStore.loading = false;
      }, this.clientErrorRenderDelay);

      this.logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'ENVERROR'), error);
    }
  });

  @action
  private createFlowForFlexSearchFetch = flow(function* (options: FetchOptions) {
    const {
      variables,
      analyticsStore,
      errorStore,
      flexibleSearchResultsStore,
      flexibleSearchStore,
      cheapestListingInfoStore,
      pageStore
    } = this.getFlexSearchFetchVariables(options.stores, options.isServer);
    const requestOptions = generateRequestOptions(options.request, options.isServer, pageStore.pageId);
    flexibleSearchResultsStore.loading = true;

    try {
      cheapestListingInfoStore.clear();
      const cabinClass = variables.searchPreferences.cabinClass;
      if (cabinClass === 'COACH' && performance) {
        performance.mark(FlexSearchPerformance.FETCH_FLEX_SEARCH_START_MARK);
      }
      const response: FlightsFlexibleSearch.FlightsSearch = yield flexibleSearchStore.fetchResults(
        variables,
        requestOptions,
        analyticsStore
      );

      const isTimeableResponse =
        response !== null &&
        response.flexibleSearchResponse !== null &&
        Object.keys(response.flexibleSearchResponse).length > 0;
      if (isTimeableResponse && cabinClass === 'COACH' && performance) {
        performance.mark(FlexSearchPerformance.FETCH_FLEX_SEARCH_END_MARK);
        performance.measure(
          FlexSearchPerformance.FETCH_FLEX_SEARCH_DURATION,
          FlexSearchPerformance.FETCH_FLEX_SEARCH_START_MARK,
          FlexSearchPerformance.FETCH_FLEX_SEARCH_END_MARK
        );
        performance.mark(FlexSearchPerformance.RENDER_FLEX_SEARCH_START_MARK);
      }

      const flexibleSearch = response?.flexibleSearchResponse;

      if (flexibleSearch) {
        flexibleSearchResultsStore.hydrate(flexibleSearch);
        flexibleSearchResultsStore.tripType = variables.flightsSearchContext.tripType;
      } else {
        flexibleSearchResultsStore.clear();
      }

      flexibleSearchResultsStore.loading = false;

      this.pendingFlexibleSearchResultsPromises = [];

      return;
    } catch (error) {
      setTimeout(() => {
        errorStore.clientFetchFailure = true;
        flexibleSearchStore.loading = false;
      }, this.clientErrorRenderDelay);

      this.logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'ENVERROR'), error);
    }
  });

  public fetchPageData(pageData: FetchPageOptions) {
    const localizer = new Localization(pageData.context.locale);
    const title = localizer.formatText('fsr.server.page.title');

    return Promise.resolve({ title: title });
  }

  private getFetchVariables(stores: StoreContainer, isServer: boolean) {
    const searchCriteriaStore = stores.get<SearchCriteriaStore>('searchCriteriaStore');
    const searchFormStore = stores.get<SearchFormStore>('searchFormStore');
    const listingsStore = stores.get<ListingsStore>('listingsStore');
    const searchResultsStore = stores.get<SearchResultsStore>('searchResults');
    const sortFilterStore = stores.get<SortFilterStore>('sortFilterStore');
    const pageTitleStore = stores.get<PageTitleStore>('pageTitleStore');
    const opinionLabStore = stores.get<OpinionLabStore>('opinionLabStore');
    const errorStore = stores.get<ErrorStore>('errorStore');
    const bannersStore = stores.get<BannersStore>('bannersStore');
    const dialogsStore = stores.get<DialogsStore>('dialogsStore');
    const bernieContext: ContextStore = stores.get<ContextStore>('context');
    const analyticsStore = stores.get<AnalyticsStore>('analytics');
    const cheapestListingInfoStore = stores.get<CheapestListingInfoStore>('cheapestListingInfoStore');
    const context = getBexApiContext(bernieContext);
    const loadSwitch = isServer ? FlightsQueryState.LOADING : FlightsQueryState.LOADED;
    searchCriteriaStore.searchCriteria.queryState = loadSwitch;
    const variables = { context, ...searchCriteriaStore.searchCriteria };
    const clientMetadataStore = stores.get<ClientMetadataStore>('clientMetadataStore');
    const gaiaIdStore = stores.get<GaiaIdStore>('gaiaIdStore');
    const stepIndicatorStore = stores.get<StepIndicatorStore>('stepIndicatorStore');
    const pageStore = stores.get<PageStore>('page');

    return {
      analyticsStore,
      searchCriteriaStore,
      searchFormStore,
      listingsStore,
      searchResultsStore,
      sortFilterStore,
      pageTitleStore,
      opinionLabStore,
      errorStore,
      bannersStore,
      dialogsStore,
      variables,
      clientMetadataStore,
      cheapestListingInfoStore,
      gaiaIdStore,
      stepIndicatorStore,
      pageStore
    };
  }

  private getFlexSearchFetchVariables(stores: StoreContainer, isServer: boolean) {
    const searchCriteriaStore = stores.get<SearchCriteriaStore>('searchCriteriaStore');
    const errorStore = stores.get<ErrorStore>('errorStore');
    const flexibleSearchResultsStore = stores.get<FlexibleSearchResultsStore>('flexibleSearchResultsStore');
    const flexibleSearchStore = stores.get<FlexibleSearchStore>('flexibleSearchStore');
    const cheapestListingInfoStore = stores.get<CheapestListingInfoStore>('cheapestListingInfoStore');
    const analyticsStore = stores.get<AnalyticsStore>('analytics');
    const bernieContext: ContextStore = stores.get<ContextStore>('context');
    const context = getBexApiContext(bernieContext);
    const loadSwitch = isServer ? FlightsQueryState.LOADING : FlightsQueryState.LOADED;
    searchCriteriaStore.flexSearchCriteria.queryState = loadSwitch;
    const pageStore = stores.get<PageStore>('page');
    const variables = { context, ...searchCriteriaStore.flexSearchCriteria };

    return {
      variables,
      analyticsStore,
      errorStore,
      flexibleSearchResultsStore,
      flexibleSearchStore,
      cheapestListingInfoStore,
      pageStore
    };
  }
}
